import React from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import useGuardedFunction from 'Hooks/useGuardedFunction';

const SmartAllocationCustomizationContext = React.createContext(undefined);

// Actions
const INITIALIZE_STARTED = 'initialize-started';
const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
const INITIALIZE_FAILED = 'initialize-failed';

const SMART_ALLOC_CUST_REFRESH_STARTED = 'smart-alloc-cust-started';
const SMART_ALLOC_CUST_REFRESH_SUCCEEDED = 'smart-alloc-cust-succeeded';
const SMART_ALLOC_CUST_REFRESH_FAILED = 'smart-alloc-cust-refresh-failed';

const UPDATE_GROUPS_COLL = 'update-sac-groups-coll';
const SET_GROUP = 'set-group';

const getGroupFromState = (groupsCollection, groupId) => {
  for (let i = 0; i < groupsCollection.resources().length(); i++) {
    if (groupsCollection.resources().index(i) === groupId) {
      return groupsCollection.resources().index(i);
    }
  }

  return null;
};

const init = () => {
  return {
    isInitializing: true,
    initializingError: null,
    // SAC
    smartAllocCustCollection: null,
    isSmartAllocCustRefreshing: false,
    smartAllocCustRefreshError: null,
    // SAC Groups
    sacGroupsCollection: null,
    isOptimisticallyUpdating: false,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        smartAllocCustCollection: action.smartAllocCustCollection,
        sacGroupsCollection: action.sacGroupsCollection,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case SMART_ALLOC_CUST_REFRESH_STARTED: {
      return {
        ...state,
        isSmartAllocCustRefreshing: true,
        smartAllocCustRefreshError: null,
      };
    }
    case SMART_ALLOC_CUST_REFRESH_SUCCEEDED: {
      return {
        ...state,
        isSmartAllocCustRefreshing: false,
        smartAllocCustRefreshError: null,
        smartAllocCustCollection: action.smartAllocCustCollection,
        sacGroupsCollection: action.sacGroupsCollection,
      };
    }
    case SMART_ALLOC_CUST_REFRESH_FAILED: {
      return {
        ...state,
        isSmartAllocCustRefreshing: false,
        smartAllocCustRefreshError: action.smartAllocCustRefreshError,
      };
    }
    case UPDATE_GROUPS_COLL: {
      return {
        ...state,
        sacGroupsCollection: action.sacGroupsCollection,
      };
    }
    case SET_GROUP: {
      const { groupResource, isOptimistic } = action;
      const groupsColl = state.sacGroupsCollection;

      const foundGroupIndex = groupsColl.resources().list().findIndex((v) => {
        return v.id() === groupResource?.id();
      });

      if (foundGroupIndex < 0) {
        return state; // no change
      }
      groupsColl.resources().replaceAtIndex(foundGroupIndex, groupResource);

      return {
        ...state,
        isOptimisticallyUpdating: isOptimistic,
        sacGroupsCollection: groupsColl,
      };
    }
    default: {
      throw new Error(`Unhandled SmartAllocationCustomizationContext action type: ${action.type}`);
    }
  }
};

const SmartAllocationCustomizationProvider = ({
  relationship,
  children,
}) => {
  const { refreshPageContentCollection, isPageContentLoading } = usePageContentContext();
  const { displayErrorMessage } = useSnackbarContext();

  const [state, dispatch] = React.useReducer(reducer, null, init);
  const {
    guardFunction, setGuardFailing, setGuardPassing,
  } = useGuardedFunction(true);

  const _refreshSacGroupsCollection = React.useCallback(async () => {
    const { sacGroupsCollection } = state;

    try {
      dispatch({ type: SMART_ALLOC_CUST_REFRESH_STARTED });

      const resp = await sacGroupsCollection.link('self').get();

      dispatch({
        type: UPDATE_GROUPS_COLL,
        sacGroupsCollection: resp,
      });
    } catch (err) {
      dispatch({ type: SMART_ALLOC_CUST_REFRESH_FAILED, smartAllocCustRefreshError: err });
    }
  }, [state]);
  const refreshSacGroupsCollection = guardFunction(_refreshSacGroupsCollection);

  const refreshSmartAllocCustCollection = React.useCallback(async () => {
    const { smartAllocCustCollection: smartAllocCustColl } = state;

    if (smartAllocCustColl === null) {
      return;
    }

    if (smartAllocCustColl.resources().length() === 0) {
      return;
    }

    const [smartAllocCustRes] = smartAllocCustColl;

    try {
      dispatch({ type: SMART_ALLOC_CUST_REFRESH_STARTED });

      const coll = await smartAllocCustRes.link('self').get();
      const [customizatizationResource] = coll;

      let sacGroupsResp = null;
      if (customizatizationResource.relationship(fr.relationships.groups.name)) {
        sacGroupsResp = await customizatizationResource.relationship(fr.relationships.groups.name).getRelated();
      }

      dispatch({
        type: SMART_ALLOC_CUST_REFRESH_SUCCEEDED,
        smartAllocCustCollection: coll,
        sacGroupsCollection: sacGroupsResp,
      });
    } catch (err) {
      dispatch({ type: SMART_ALLOC_CUST_REFRESH_FAILED, smartAllocCustRefreshError: err });
    }
  }, [state]);

  const loadSmartAllocCustCollection = async () => {
    try {
      dispatch({ type: INITIALIZE_STARTED });

      const resp = await relationship.link('related').get();
      const [customizatizationResource] = resp;

      let sacGroupsResp = null;
      if (customizatizationResource.relationship(fr.relationships.groups.name)) {
        sacGroupsResp = await customizatizationResource.relationship(fr.relationships.groups.name).getRelated();
      }

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        smartAllocCustCollection: resp,
        sacGroupsCollection: sacGroupsResp,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_FAILED, initializationError: err });
    }
  };

  useComponentDidMount(loadSmartAllocCustCollection);

  // Refresh the groups whenever the page is loading
  React.useEffect(() => {
    if (isPageContentLoading) {
      refreshSacGroupsCollection();
    }
  }, [isPageContentLoading]);

  const optimisticUpdateSacGroup = async (groupsColl, sacGroupResource) => {
    dispatch({ type: SET_GROUP, groupResource: sacGroupResource, isOptimistic: true });
    try {
      const [updatedGroupResource] = await sacGroupResource.submit();
      setGuardFailing();
      refreshPageContentCollection().then(() => {
        setGuardPassing();
        // Wait until after the refresh to dispatch this update - that way, we'll hold the isOptimisticallyUpdating
        // state until rendering is done and avoid issues where someone can grab and hold a draggable sac step through a
        // refresh and cause an unplug when the groups are re-rendered.
        dispatch({ type: SET_GROUP, groupResource: updatedGroupResource, isOptimistic: false });
      });
    } catch (err) {
      dispatch({
        type: SET_GROUP,
        groupResource: getGroupFromState(state.sacGroupsCollection, sacGroupResource.id()),
        isOptimistic: false,
      });
      displayErrorMessage(err, err.errors);
    }
  };

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      smartAllocCustCollection: state.smartAllocCustCollection,
      smartAllocCustResource: state.smartAllocCustCollection?.resources().head(), // collection is null to start
      isSmartAllocCustRefreshing: state.isSmartAllocCustRefreshing,
      smartAllocCustRefreshError: state.smartAllocCustRefreshError,
      sacGroupsCollection: state.sacGroupsCollection,
      isOptimisticallyUpdating: state.isOptimisticallyUpdating,
      refreshSmartAllocCustCollection,
      optimisticUpdateSacGroup,
    };
  }, [
    state,
    refreshSmartAllocCustCollection,
    optimisticUpdateSacGroup,
  ]);

  return (
    <SmartAllocationCustomizationContext.Provider value={memoizedValue}>
      {children}
    </SmartAllocationCustomizationContext.Provider>
  );
};

// Use SmartAllocationCustomization State Context
const useSmartAllocationCustomizationContext = () => {
  const context = React.useContext(SmartAllocationCustomizationContext);
  if (context === undefined) {
    throw new Error('useSmartAllocationCustomizationContext must be used within a SmartAllocationCustomizationProvider');
  }
  return context;
};

SmartAllocationCustomizationProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  relationship: PropTypes.object.isRequired,
};

export {
  SmartAllocationCustomizationProvider,
  useSmartAllocationCustomizationContext,
};
